import { useState, useCallback, useEffect, useRef } from 'react';
import { Admin } from 'type/type';
import { decryptAES256, getAdminList } from 'api/api';
import Pagenation from 'components/common/Pagenation';
import { formattedDateTime } from 'pages/utils/commonUtils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PrimaryButton } from 'components/common/Button';
import { CommonTable, CommonTableWrap, FilterContentContainer } from 'pages/Layout';

interface SearchInputType {
  type: string,
  value: string,
  is_contain: string,
  page?: number,
};

function AdminList() {
  const isFirstRender = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();
  // 필터obj
  const [searchObj, setSearchObj] = useState<SearchInputType>({
    type: searchParams.get("type") || "id",
    value: searchParams.get("value") || "",
    is_contain: "1",
    page: searchParams.get("page") ? Number(searchParams.get("page")) : undefined,
  });
  const [reqState, setReqState] = useState<SearchInputType|null>(null); // 현재 페이지
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<Admin[]>([]);
  // 페이지네이션
  const buttonPerPage = 10; // 보여줄 페이지네이션 버튼 수
  const [page, setPage] = useState(searchObj.page || 1); // 현재 페이지
  const [limit] = useState(10); // 보여줄 게시물 개수
  const [totalCount, setTotalCount] = useState(-1);
  const numPages = Math.ceil(totalCount / limit); // 총 페이지 수
  const [startButtonNum, setStartButtonNum] = useState(1); // 버튼 시작 숫자

  // 페이지네이션 버튼 클릭 시
  const isClickButton = (str:string) => {
    if (str === 'prev') handlePrevPage();
    else handleNextPage();
  };

  // 이전페이지 이동(buttonPerPage만큼 -)
  const handlePrevPage = () => {
    const prevPage = Math.max(startButtonNum - buttonPerPage, 1);
    setStartButtonNum(prevPage);
    setPage(prevPage);
  };

  // 다음페이지 이동(buttonPerPage만큼 +)
  const handleNextPage = () => {
    const nextPage = Math.min(startButtonNum + buttonPerPage, numPages);
    setStartButtonNum(nextPage);
    setPage(nextPage);
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') searchHandle();
  }

  const searchHandle = () => {
    page === 1 ? getAdmins(page, limit) : setPage(1);
  }

  const REACT_APP_SECRET_KEY = process.env.REACT_APP_SECRET_KEY!;
  const REACT_APP_SECRET_IV = process.env.REACT_APP_SECRET_IV!;
  const getAdmins = useCallback(async (page: number, limit: number) => {
    const { type, value, is_contain} = searchObj;
    const param: Record<string, string> = { [type]: value, is_contain };
    const adminRes = await getAdminList(page, limit, param);
    if (adminRes.code === 200) {
      setAdmins(adminRes.data);
      setTotalCount(adminRes.total_count);
    }
    setReqState(searchObj);
    setSearchParams({ type, value, is_contain, page : String(page) });
  }, [searchObj]);
  
  // 검색 type 변경
  const handleSearchType = (type: string) => {
    setSearchObj(prev => ({...prev, type, value: "" }));
  };
  // 검색 value 변경
  const handleSearchValue = (value: string) => {
    if (searchObj.type === "id") value = value.toLowerCase();
    setSearchObj(prev => ({...prev, value}));
  };
  
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const hasParams = searchParams.toString() !== "";
      if (!hasParams) return; // ❌ 파라미터 없으면 첫 렌더 스킵
    }
    getAdmins(page, limit); // ✅ 이후에는 무조건 실행
  }, [page, limit]);
  return (
    <>
      <FilterContentContainer>
        <div className='flex-row'>
          <label>검색</label>
          <div>
            <select name="search_type" onChange={(e) => handleSearchType(e.target.value)} value={searchObj.type} >
              <option value="id">ID</option>
              <option value="name">이름</option>
            </select>
          </div>
          <div>
            <input type="text" name="search_value" onChange={(e) => handleSearchValue(e.target.value)} value={searchObj.value} placeholder="검색 내용 입력" onKeyUp={handleKeyDown} />
          </div>
        </div>
        <PrimaryButton type="button" onClick={searchHandle}>조회</PrimaryButton>
        <div className='flex-row btn-wrap'>
          <PrimaryButton type="button" onClick={() => navigate('/admin/mgmt/register', { state: reqState})}>등록</PrimaryButton>
        </div>
      </FilterContentContainer>
      <div>
        검색 결과: <span style={{ fontWeight: "600" }}>{totalCount < 0 ? 0 : totalCount}</span>건
      </div>
        <CommonTableWrap>
          <CommonTable>
            <thead>
              <tr>
                <th className='w-5'>No</th>
                <th className='w-10'>권한</th>
                <th className='w-10'>ID</th>
                <th className='w-15'>이름</th>
                <th className='w-15'>연락처</th>
                <th className='w-15'>E-MAIL</th>
                <th className='w-10'>승인상태</th>
                <th className='w-15'>생성일자</th>
              </tr>
            </thead>
            <tbody>
              {totalCount === 0 ?
                <tr><td colSpan={8}>검색결과가 없습니다.</td></tr>
                :
                admins.map((admin, idx) => 
                  <tr key={`admin_${idx}`} onClick={() => navigate(`/admin/mgmt/edit/${admin.id}`, { state: reqState })}>
                    <td>{(page - 1) * 10 + idx + 1}</td>
                    <td>{admin.auth_group}</td>
                    <td className='ellipsis-text'>{admin.id}</td>
                    <td className='ellipsis-text'>{admin.name}</td>
                    <td className='ellipsis-text'>{decryptAES256(admin.mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!)}</td>
                    <td className='ellipsis-text'>{admin.email}</td>
                    <td>{admin.approval_status === 0 ? '승인대기' : admin.approval_status === 1 ? '승인' : '미승인'}</td>
                    <td>{formattedDateTime(admin.create_date)}</td>
                  </tr>
                )
              }
            </tbody>
          </CommonTable>
        </CommonTableWrap>
        <Pagenation
          totalCount={totalCount}
          total={numPages}
          limit={limit}
          page={page}
          setPage={setPage}
          startButtonNum={startButtonNum}
          setClickButton={isClickButton}
        />
    </>
  );
}

export default AdminList;